import React, { useState, useEffect, useContext, useRef } from "react";
import { ToastContainer } from "react-toastify";
import useSwal from '../../../common/ReusableComponent/useSwal';
import { useToastHandler } from '../../../common/ToastHandler';
import ReusableTable from "../../../common/ReusableComponent/reusableTable/ReusableTable";
import ReusableModal from "../../../../components/Pages/AppPAges/ManageSettings/ReusableModal";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import { CustomButton } from "../../../common/ReusableComponent/CustomButton";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import DocumentPreviewModal from "../../../common/ReusableComponent/DocumentPreviewModal";
import PaginatedSearchSelect from "../../../../components/common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import { ReusableInput } from "../../../../components/common/ReusableComponent/ReusableInput";

const parseTemplateMeta = (rawContent) => {
  try {
    const parsed = typeof rawContent === "string" ? JSON.parse(rawContent) : rawContent;
    return parsed?.legal_meta || parsed || {};
  } catch {
    return {};
  }
};

const findTemplateVariables = (template = {}) => {
  const metaVariables = parseTemplateMeta(template.raw_content).variables || [];
  const htmlVariables = (String(template.content || "").match(/{{\s*[\w.]+\s*}}/g) || [])
    .map((item) => item.replace(/[{}]/g, "").trim())
    .filter((key) => !key.startsWith("sig:"))
    .map((key) => ({ key, label: key.replace(/_/g, " "), data_type: "text", required: false }));
  const byKey = new Map([...htmlVariables, ...metaVariables].map((variable) => [variable.key, variable]));
  return [...byKey.values()];
};

const findSignerRoles = (template = {}) => {
  const meta = parseTemplateMeta(template.raw_content);
  return meta.signer_roles?.length ? meta.signer_roles : ["Signer"];
};


const DocumentSigning = () => {
  const [loading, setLoading] = useState(false);
  const { GetPaginatedData, PostFormData, PostWithId, getHost, Delete, Post } = useApi();
  const { user } = useContext(AuthContext);
  const { handleToast } = useToastHandler();
  const { confirmDelete } = useSwal();
  const [showModal, setShowModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [users, setUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [templates, setTemplates] = useState([]);
  const fileInputRef = useRef(null);
  const multiShareFileInputRef = useRef(null);

  const [showMultiShareModal, setShowMultiShareModal] = useState(false);
  const [multiShareData, setMultiShareData] = useState({
    manager_id: "",
    manager_name: "",
    employee_id: "",
    employee_name: "",
    template_id: "",
    template_name: "",
    document_file: null
  });

  const [showAssetModal, setShowAssetModal] = useState(false);
  const [assetData, setAssetData] = useState({
    asset_id: "",
    asset_name: "",
    category_id: "",
    category_name: "",
    barcode: "",
    notes: ""
  });

  const [formData, setFormData] = useState({
    title: "",
    manager_id: "",
    user_id: "",
    user_name: "",
    party_email: "",
    template_id: "",
    template_name: "",
    document_file: null,
    status: "draft"
  });

  const [uploadedDocPreview, setUploadedDocPreview] = useState(null);
  const [multiShareUploadedPreview, setMultiShareUploadedPreview] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState([]);
  const [variableValues, setVariableValues] = useState({});
  const [signerEmails, setSignerEmails] = useState({ Signer: "" });

  const resetFormData = () => {
    setFormData({
      title: "",
      manager_id: "",
      user_id: "",
      user_name: "",
      party_email: "",
      template_id: "",
      template_name: "",
      document_file: null,
      status: "draft"
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (uploadedDocPreview) {
      URL.revokeObjectURL(uploadedDocPreview);
      setUploadedDocPreview(null);
    }
    setSelectedTemplate(null);
    setTemplateVariables([]);
    setVariableValues({});
    setSignerEmails({ Signer: "" });
  };

  const resetMultiShareData = () => {
    setMultiShareData({
      manager_id: "",
      manager_name: "",
      employee_id: "",
      employee_name: "",
      template_id: "",
      template_name: "",
      document_file: null
    });
    if (multiShareFileInputRef.current) {
      multiShareFileInputRef.current.value = "";
    }
    if (multiShareUploadedPreview) {
      URL.revokeObjectURL(multiShareUploadedPreview);
      setMultiShareUploadedPreview(null);
    }
  };

  const handleViewDocument = (documentFile) => {
    if (!documentFile) return;
    const fullUrl = `${getHost()}/${documentFile}`;
    setPreviewUrl(fullUrl);
    setIsPreviewOpen(true);
  };


  const fetchSigningDocuments = async ({ page = 1, searchTerm = "" } = {}) => {
    try {
      const response = await GetPaginatedData('document', {
        page,
        search: searchTerm
      });
      return {
        data: response.data,
        totalPages: Math.ceil(response.totalCount / 10)
      };
    } catch (error) {
      console.error('Error fetching documents:', error);
      return {
        data: [],
        totalPages: 1
      };
    }
  };


  const handleCreateDocument = async () => {
    setFormData({
      title: "",
      manager_id: "",
      user_id: "",
      user_name: "",
      party_email: "",
      template_id: "",
      template_name: "",
      document_file: null,
      status: "draft"
    });
    setShowModal(true);
    try {
      const templateResponse = await GetPaginatedData('template');
      setTemplates(templateResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleMultiShareTemplate = async () => {
    setMultiShareData({
      manager_id: "",
      manager_name: "",
      employee_id: "",
      employee_name: "",
      template_id: "",
      template_name: "",
      document_file: null
    });
    setShowMultiShareModal(true);
    try {
      const userResponse = await GetPaginatedData('user');
      const storeEmployees = userResponse.data.filter(user => user.ms_role_name === "store employee");
      const storeManagers = userResponse.data.filter(user => user.ms_role_name === "store manager");
      setUsers(storeEmployees);
      setManagers(storeManagers);
      const templateResponse = await GetPaginatedData('template');
      setTemplates(templateResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleMultiShareInputChange = (e) => {
    const { name, value } = e.target;
    setMultiShareData(prev => ({
      ...prev,
      [name]: value
    }));
  };



  const handleCheckAsset = () => {
    setAssetData({
      asset_id: "",
      asset_name: "",
      category_id: "",
      category_name: "",
      barcode: "",
      notes: ""
    });
    setShowAssetModal(true);
  };

  const handleAssetSubmit = async () => {
    if (!assetData.asset_id) {
      handleToast({
        type: "ERROR",
        message: "Please select an asset"
      });
      return;
    }
    try {
      setLoading(true);
      handleToast({
        type: "SUCCESS",
        message: "Asset checked successfully"
      });
      setShowAssetModal(false);
    } catch (error) {
      console.error("Error checking asset:", error);
      handleToast({
        type: "ERROR",
        message: "Error checking asset"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleShareTemplate = async () => {
    if (!multiShareData.template_id && !multiShareData.document_file) {
      handleToast({
        type: "ERROR",
        message: "Please select a template or upload a document"
      });
      return;
    }

    const allUserIds = [];
    if (multiShareData.employee_id) {
      allUserIds.push(multiShareData.employee_id);
    }
    if (multiShareData.manager_id) {
      allUserIds.push(multiShareData.manager_id);
    }

    if (allUserIds.length === 0) {
      handleToast({
        type: "ERROR",
        message: "Please select at least one user"
      });
      return;
    }

    try {
      setLoading(true);

      // First create document from template
      const documentPayload = new FormData();
      documentPayload.append("title", `Shared Template Document`);
      if (multiShareData.template_id) {
        documentPayload.append("template_id", multiShareData.template_id);
      }
      documentPayload.append("status", "draft");
      documentPayload.append("store_id", user.selected_store || user.main_store);
      if (multiShareData.document_file) {
        documentPayload.append("document_file", multiShareData.document_file);
      }

      const documentResponse = await PostFormData("document", documentPayload);
      const documentId = documentResponse.data.id;

      // Then share document with users using new endpoint
      const sharePayload = {
        document_id: documentId,
        user_ids: allUserIds.map(id => parseInt(id))
      };

      await Post('sharedSingle', sharePayload);

      handleToast({
        type: "SUCCESS",
        message: `Template shared with ${allUserIds.length} user(s) successfully`
      });

      setShowMultiShareModal(false);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error("Error sharing template:", error);
      handleToast({
        type: "ERROR",
        message: "Error sharing template"
      });
    } finally {
      setLoading(false);
    }
  };


  // const handleUserSelect = (userId) => {
  //   setFormData(prev => {
  //     const isSelected = prev.user_id.includes(userId);
  //     return {
  //       ...prev,
  //       user_ids: isSelected
  //         ? prev.user_id.filter(id => id !== userId)
  //         : [...prev.user_id, userId]
  //     };
  //   });
  // };


  // const removeUser = (userId) => {
  //   setFormData(prev => ({
  //     ...prev,
  //     user_ids: prev.user_id.filter(id => id !== userId)
  //   }));
  // };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      document_file: file
    }));
    if (file) {
      setUploadedDocPreview(URL.createObjectURL(file));
    }
  };

  const handleViewUploadedDoc = () => {
    if (uploadedDocPreview) {
      window.open(uploadedDocPreview, '_blank');
    }
  };

  const handleMultiShareFileChange = (e) => {
    const file = e.target.files[0];
    setMultiShareData(prev => ({
      ...prev,
      document_file: file
    }));
    if (file) {
      setMultiShareUploadedPreview(URL.createObjectURL(file));
    }
  };

  const handleViewMultiShareUploadedDoc = () => {
    if (multiShareUploadedPreview) {
      window.open(multiShareUploadedPreview, '_blank');
    }
  };


  const handleSaveDocument = async () => {
    const partyEmail = (formData.party_email || "").trim().toLowerCase();
    const roleEmails = Object.values(signerEmails).map((email) => String(email || "").trim().toLowerCase()).filter(Boolean);
    const candidateEmails = roleEmails.length ? roleEmails : [partyEmail];
    const invalidEmail = candidateEmails.find((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    const missingVariables = templateVariables.filter((variable) => variable.required && !String(variableValues[variable.key] || variable.default_value || "").trim());

    if (!formData.title || candidateEmails.length === 0 || invalidEmail) {
      handleToast({
        type: "ERROR",
        message: "Please enter document title and valid signer email"
      });
      return;
    }
    if (missingVariables.length) {
      handleToast({
        type: "ERROR",
        message: `Missing required values: ${missingVariables.map((variable) => variable.label || variable.key).join(", ")}`
      });
      return;
    }
    try {
      setLoading(true);
      const payload = new FormData();
      payload.append("title", formData.title);
      if (formData.template_id) {
        payload.append("template_id", formData.template_id);
      }
      payload.append("status", formData.status);
      payload.append("store_id", user.selected_store || user.main_store);
      payload.append("variables", JSON.stringify(variableValues));
      payload.append("signer_map", JSON.stringify(signerEmails));
      if (formData.document_file) {
        payload.append("document_file", formData.document_file);
      }
      const documentResponse = await PostFormData("document", payload);
      const documentId = documentResponse.data.id;

      const assignmentEmails = [...new Set(Object.values(signerEmails).map((email) => String(email || "").trim().toLowerCase()).filter(Boolean))];
      for (const email of assignmentEmails.length ? assignmentEmails : [partyEmail]) {
        const assignPayload = {
          store_id: user.selected_store || user.main_store,
          email,
          party_email: email,
          assigned_email: email,
        };
        await PostWithId(
          "document",
          `${documentId}/assign_to_user/?email=${encodeURIComponent(email)}`,
          assignPayload
        );
      }
      handleToast({
        type: "SUCCESS",
        message: `Document assigned to ${assignmentEmails.length || 1} signer(s) successfully`
      });
      setRefreshKey(prev => prev + 1);
      setShowModal(false);
    } catch (error) {
      console.error("Error creating/assigning document:", error);
      handleToast({
        type: "ERROR",
        message: "Error creating or assigning document"
      });
    } finally {
      setLoading(false);
    }
  };
  const handleOnDelete = async (id) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) return;
    try {
      const response = await Delete('document', id);
      handleToast({
        type: "SUCCESS",
        message: response.message || 'Document deleted successfully'
      });
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting document:', error);
      handleToast({
        type: "ERROR",
        message: 'Error deleting document'
      });
    }
  };
  const columns = [
    { header: "Title", field: "title" },
    { header: "Status", field: "status" },
    // {
    //   header: "Document File",
    //   field: "document_file",
    //   render: (row) => (
    //     row.document_file ? (
    //       <button
    //         onClick={() => handleViewDocument(row.document_file)}
    //         style={{
    //           padding: "6px 12px",
    //           backgroundColor: "#007bff",
    //           color: "white",
    //           border: "none",
    //           borderRadius: "6px",
    //           cursor: "pointer",
    //           fontSize: "12px",
    //           fontWeight: "500"
    //         }}
    //       >
    //         📄 View Document
    //       </button>
    //     ) : 'No file'
    //   )
    // }
  ];
  return (
    <>
      <ToastContainer />
      <div className="responsive-box">
        <div className="inventory-header">
          <div className="inventory-title">
            <h2>Legal Document Management </h2>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>

            <CustomButton
              text="Assign Template (Single Party)"
              onClick={handleCreateDocument}
              color="#000000"
              backgroundColor="#FFAF32"
            />
            <CustomButton
              text="Assign Template (Multi-Party)"
              onClick={handleMultiShareTemplate}
              color="#FFFFFF"
              backgroundColor="#4A5546"
            />
          </div>
        </div>
        <ReusableTable
          key={refreshKey}
          fetchData={fetchSigningDocuments}
          columns={columns}
          showAction={true}
          showFooter={true}
          onDelete={handleOnDelete}
          showDelete={true}
          loading={loading}
        />
        <ReusableModal
          show={showModal}
          handleClose={() => {
            setShowModal(false);
            setShowUserDropdown(false);
            resetFormData();
          }}

          title="Create Legal Document Assign"
          width="500px"
        >
          <div style={{ padding: "20px" }}>
            <ReusableInput
              type="text"
              name="title"
              label="Document Title"
              value={formData.title}
              onChange={handleInputChange}
              required
              maxLength={100}
            // style={{ width: "300px" }}

            />

            <div style={{ marginBottom: "15px" }}>
              <ReusableInput
                type="email"
                name="party_email"
                label="Assign to Party Email"
                value={formData.party_email}
                onChange={handleInputChange}
                required
                maxLength={254}
              />
              <small style={{ color: "#667085", display: "block", marginTop: "6px" }}>
                Enter the signer email directly. The party does not need to appear in the user list.
              </small>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <PaginatedSearchSelect
                api="template"
                GetPaginatedData={GetPaginatedData}
                getOptionLabel={(opt) => opt.name}
                onSelect={(opt) => {
                  const roles = findSignerRoles(opt || {});
                  const variables = findTemplateVariables(opt || {});
                  setFormData(prev => ({
                    ...prev,
                    template_id: opt?.id || "",
                    template_name: opt?.name || ""
                  }));
                  setSelectedTemplate(opt || null);
                  setTemplateVariables(variables);
                  setVariableValues(
                    variables.reduce((acc, variable) => {
                      acc[variable.key] = variable.default_value || "";
                      return acc;
                    }, {})
                  );
                  setSignerEmails(
                    roles.reduce((acc, role) => {
                      acc[role] = "";
                      return acc;
                    }, {})
                  );
                }}
                placeholder="Select Legal Document"
                pageSize={10}
                value={formData.template_id ? { id: formData.template_id, name: formData.template_name } : null}
              />
            </div>

            {templateVariables.length > 0 && (
              <div style={{ marginBottom: "15px", padding: "14px", border: "1px solid #e5e7eb", borderRadius: "12px", background: "#f8fafc" }}>
                <strong>Template Information</strong>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", marginTop: "10px" }}>
                  {templateVariables.map((variable) => (
                    <label key={variable.key} style={{ fontSize: "13px", color: "#475569" }}>
                      {variable.label || variable.key}{variable.required ? " *" : ""}
                      {variable.data_type === "multiline" || variable.data_type === "address" ? (
                        <textarea
                          value={variableValues[variable.key] || ""}
                          onChange={(e) => setVariableValues((prev) => ({ ...prev, [variable.key]: e.target.value }))}
                          rows={2}
                          style={{ width: "100%", marginTop: "5px", padding: "8px", borderRadius: "8px", border: "1px solid #d1d5db" }}
                        />
                      ) : (
                        <input
                          type={["date", "email", "number"].includes(variable.data_type) ? variable.data_type : "text"}
                          value={variableValues[variable.key] || ""}
                          onChange={(e) => setVariableValues((prev) => ({ ...prev, [variable.key]: e.target.value }))}
                          style={{ width: "100%", marginTop: "5px", padding: "8px", borderRadius: "8px", border: "1px solid #d1d5db" }}
                        />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(signerEmails).length > 0 && selectedTemplate && (
              <div style={{ marginBottom: "15px", padding: "14px", border: "1px solid #fed7aa", borderRadius: "12px", background: "#fff7ed" }}>
                <strong>Signer Roles</strong>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", marginTop: "10px" }}>
                  {Object.keys(signerEmails).map((role) => (
                    <label key={role} style={{ fontSize: "13px", color: "#7c2d12" }}>
                      {role} Email *
                      <input
                        type="email"
                        value={signerEmails[role] || ""}
                        onChange={(e) => setSignerEmails((prev) => ({ ...prev, [role]: e.target.value }))}
                        style={{ width: "100%", marginTop: "5px", padding: "8px", borderRadius: "8px", border: "1px solid #fdba74" }}
                      />
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: "15px" }}>
              <label><strong>Upload Document (Optional)</strong></label>
              <input
                key={showModal ? 'file-input-open' : 'file-input-closed'}
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                accept=".doc,.docx,.pdf"
                style={{
                  width: "100%",
                  padding: "10px",
                  marginTop: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc"
                }}
              />
              <small style={{ color: "#666", fontSize: "12px" }}>
                Accepted formats: DOC, DOCX, PDF
              </small>
              {formData.document_file && (
                <div style={{ marginTop: "10px" }}>
                  <button
                    type="button"
                    onClick={handleViewUploadedDoc}
                    style={{
                      padding: "6px 12px",
                      backgroundColor: "#17a2b8",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px"
                    }}
                  >
                    📄 View Uploaded Document
                  </button>
                </div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <CustomButton
                text="Cancel"
                onClick={() => setShowModal(false)}
                backgroundColor="#6c757d"
              />
              <CustomButton
                text={loading ? "Assigning Document..." : "Create & Assign Document"}
                onClick={handleSaveDocument}
                backgroundColor="#FFAF32"
              />
            </div>
          </div>
        </ReusableModal>

        <ReusableModal
          show={showMultiShareModal}
          handleClose={() => {
            setShowMultiShareModal(false);
            resetMultiShareData();
          }}

          title="Share Document with Multiple Parties"
          width="500px"
        >
          <div style={{ padding: "20px" }}>
            <div style={{ marginBottom: "15px" }}>
              <PaginatedSearchSelect
                api="template"
                GetPaginatedData={GetPaginatedData}
                getOptionLabel={(opt) => opt.name}
                onSelect={(opt) => {
                  setMultiShareData(prev => ({
                    ...prev,
                    template_id: opt?.id || "",
                    template_name: opt?.name || ""
                  }));
                }}
                placeholder="Select Legal Document"
                pageSize={10}
                value={multiShareData.template_id ? { id: multiShareData.template_id, name: multiShareData.template_name } : null}
              />
            </div>

            <div style={{ marginBottom: "15px" }}>
              <PaginatedSearchSelect
                api="user"
                GetPaginatedData={GetPaginatedData}
                getOptionLabel={(opt) => `${opt.first_name} ${opt.last_name}`}
                onSelect={(opt) => {
                  setMultiShareData(prev => ({
                    ...prev,
                    manager_id: opt?.id || "",
                    manager_name: `${opt?.first_name} ${opt?.last_name}`
                  }));
                }}
                placeholder="Select Manager"
                pageSize={10}
                value={multiShareData.manager_id ? { id: multiShareData.manager_id, first_name: multiShareData.manager_name.split(' ')[0], last_name: multiShareData.manager_name.split(' ')[1] } : null}
                filterFn={(user) => user.ms_role_name === "store manager"}
              />
            </div>

            <div style={{ marginBottom: "15px" }}>
              <PaginatedSearchSelect
                api="user"
                GetPaginatedData={GetPaginatedData}
                getOptionLabel={(opt) => `${opt.first_name} ${opt.last_name}`}
                onSelect={(opt) => {
                  setMultiShareData(prev => ({
                    ...prev,
                    employee_id: opt?.id || "",
                    employee_name: `${opt?.first_name} ${opt?.last_name}`
                  }));
                }}
                placeholder="Select Party"
                pageSize={10}
                value={multiShareData.employee_id ? { id: multiShareData.employee_id, first_name: multiShareData.employee_name.split(' ')[0], last_name: multiShareData.employee_name.split(' ')[1] } : null}
                filterFn={(user) => user.ms_role_name === "store employee"}
              />
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label><strong>Upload Document (Optional)</strong></label>
              <input
                key={showMultiShareModal ? 'multi-file-input-open' : 'multi-file-input-closed'}
                ref={multiShareFileInputRef}
                type="file"
                onChange={handleMultiShareFileChange}
                accept=".doc,.docx,.pdf"
                style={{
                  width: "100%",
                  padding: "10px",
                  marginTop: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc"
                }}
              />
              <small style={{ color: "#666", fontSize: "12px" }}>
                Accepted formats: DOC, DOCX, PDF
              </small>
              {multiShareData.document_file && (
                <div style={{ marginTop: "10px" }}>
                  <button
                    type="button"
                    onClick={handleViewMultiShareUploadedDoc}
                    style={{
                      padding: "6px 12px",
                      backgroundColor: "#17a2b8",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px"
                    }}
                  >
                    📄 View Uploaded Document
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
              <CustomButton
                text="Cancel"
                onClick={() => setShowMultiShareModal(false)}
                backgroundColor="#6c757d"
              />
              <CustomButton
                text={loading ? "Sharing..." : "Share Template"}
                onClick={handleShareTemplate}
                backgroundColor="#FFAF32"
                disabled={loading}
              />
            </div>
          </div>
        </ReusableModal>

        <DocumentPreviewModal
          isOpen={isPreviewOpen}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewUrl(null);
          }}
          fileUrl={previewUrl}
        />
      </div>
    </>
  );
};
export default DocumentSigning;
